import React, { useState, useEffect, useMemo } from 'react';
import {
  FormControl,
  Select,
  IPaginationState,
  Pagination,
} from '@uhg-harmony/uitk-react';
import { useTrackWidget } from 'shared/util/analytics/useTrackWidget';
import styled, { useTheme } from 'styled-components';
import { useLocation } from 'react-router-dom';
import SearchBarWidget from './SearchBarWidget';
import {
  truncateDescription,
  highlightKeyword,
  getContrastColor,
} from './Search.util';

const ResultsContainer = styled.div<{
  pageBackground: string;
  textColor: string;
}>`
  width: 100%;
  margin: 12vh auto 0 auto;
  padding: 20px;
  background-color: ${props => props.pageBackground};
  color: ${props => props.textColor};
   font-family: Arial, sans-serif;
   display: flex;
   flex-direction: column;
   flex-grow: 1;
   height: auto;
`;

const Title = styled.h2`
  font-size: 22px;
  margin-left: 20px;
`;

const SortDropdown = styled(Select)<{
  pageBackground: string;
  textColor: string;
}>`
  margin-left: 10px;
  padding: 5px;
  border-radius: 4px;
  background-color: ${props => props.pageBackground};
  color: ${props => props.textColor};
  border: 1px solid #444;
  cursor: pointer;
  transition: all 0.2s ease;
  width: auto;
  max-width: 200px;
  min-width: 150px;
  display: inline-block;
  &:hover {
    border-color: #007bff;
  }
`;

const ResultsList = styled.div`
  margin-top: 20px;
`;

/**
 * Each result card is a full-width anchor so the entire card is clickable,
 * navigating to result.link (which may include a hash anchor from the API).
 */
const ResultItem = styled.a<{ pageBackground: string; accentColor: string }>`
  display: block;
  padding: 15px;
  border-bottom: 1px solid rgba(128, 128, 128, 0.3);
  background-color: ${props => props.pageBackground};
  text-decoration: none;
  outline: none;
  cursor: pointer;

  &:hover,
  &:focus-visible {
    filter: brightness(0.92);
    outline: 2px solid ${props => props.accentColor};
    outline-offset: -2px;
  }
`;

/** Title rendered as a styled span — the parent <a> handles navigation. */
const ResultTitle = styled.span<{ accentColor: string }>`
  font-size: 18px;
  color: ${props => props.accentColor};
  display: block;
  margin-bottom: 4px;

  ${ResultItem}:hover &,
  ${ResultItem}:focus-visible & {
    text-decoration: underline;
  }
`;

const Description = styled.p<{ $expanded: boolean; textColor: string }>`
  margin: 5px 0;
  color: ${props => props.textColor};
  font-size: 14px;
  line-height: 1.4;
  opacity: 0.8;
  /* Only clamp when collapsed */
  display: -webkit-box;
  -webkit-line-clamp: ${props => (props.$expanded ? 'unset' : 2)};
  -webkit-box-orient: vertical;
  overflow: ${props => (props.$expanded ? 'visible' : 'hidden')};
`;

const SeeMoreButton = styled.button<{ accentColor: string }>`
  background: none;
  border: none;
  padding: 0;
  margin-top: 4px;
  color: ${props => props.accentColor};
  font-size: 13px;
  cursor: pointer;
  display: inline-block;

  &:hover {
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px solid ${props => props.accentColor};
    outline-offset: 2px;
    border-radius: 2px;
  }
`;

const DateUpdated = styled.div<{ textColor: string }>`
  font-size: 12px;
  margin-top: 4px;
  color: ${props => props.textColor};
  opacity: 0.6;
`;

const ControlsContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 20px;
  flex-wrap: wrap;
  gap: 20px;
`;

const DESCRIPTION_MAX_LENGTH = 200;

/**
 * Returns true when the URL is absolute (http:// or https://).
 * Used to open external links in a new tab with the appropriate
 * security attributes (noopener noreferrer).
 *
 * @param url - The href string to evaluate.
 * @returns `true` for absolute external URLs.
 */
const isExternalUrl = (url: string): boolean => /^https?:\/\//i.test(url);

interface SearchResult {
  category: string;
  title: string;
  description: string;
  link: string;
  dateUpdated: string;
}

interface SearchResultCardProps {
  result: SearchResult;
  searchQuery: string;
  cardBackground: string;
  accentColor: string;
  textColor: string;
  onResultClick: () => void;
}

/**
 * Individual search result card with an independent expand/collapse toggle
 * for the description snippet.
 *
 * The "See more" button calls stopPropagation so it does not trigger the
 * parent anchor's navigation while still toggling visibility.
 */
const SearchResultCard: React.FC<SearchResultCardProps> = ({
  result,
  searchQuery,
  cardBackground,
  accentColor,
  textColor,
  onResultClick,
}) => {
  const [expanded, setExpanded] = useState(false);
  const isTruncatable = result.description.length > DESCRIPTION_MAX_LENGTH;
  const visibleDescription = expanded
    ? result.description
    : truncateDescription(result.description, DESCRIPTION_MAX_LENGTH);

  const handleToggle = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setExpanded(prev => !prev);
  };

  return (
    <ResultItem
      href={result.link}
      pageBackground={cardBackground}
      accentColor={accentColor}
      onClick={onResultClick}
      target={isExternalUrl(result.link) ? '_blank' : undefined}
      rel={isExternalUrl(result.link) ? 'noopener noreferrer' : undefined}
    >
      <ResultTitle accentColor={accentColor}>
        {highlightKeyword(result.title, searchQuery)}
      </ResultTitle>
      <Description $expanded={expanded} textColor={textColor}>
        {highlightKeyword(visibleDescription, searchQuery)}
      </Description>
      {isTruncatable && (
        <SeeMoreButton
          data-test-id="see-more-toggle"
          aria-expanded={expanded}
          accentColor={accentColor}
          onClick={handleToggle}
        >
          {expanded ? 'See less' : 'See more'}
        </SeeMoreButton>
      )}
      <DateUpdated textColor={textColor}>
        Updated: {result.dateUpdated}
      </DateUpdated>
    </ResultItem>
  );
};

interface SearchResultsProps {
  fetchSearchResults: (searchQuery: string) => Promise<SearchResult[]>;
  searchResults?: SearchResult[];
  currentPage?: any;
  searchWidgetState?: any;
  site?: Site;
}
const SearchResults: React.FC<SearchResultsProps> = ({
  fetchSearchResults,
  currentPage,
  searchWidgetState,
  site,
}) => {
  const trackSearchResults = useTrackWidget({
    tracking: (currentPage as any)?.tracking,
    widgetState: (currentPage as any)?.widgetState ?? {
      id: 'searchResults',
      config: { analyticsLabel: undefined },
    },
    currentPage: currentPage,
    site: (currentPage as any)?.site,
  });
  const location = useLocation();
  const searchQuery = new URLSearchParams(location.search).get('q') || '';
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [sortBy, setSortBy] = useState('relevance');
  const theme: any = useTheme();
  // The theme's quaternary color is the designated page-background token across
  // all brand themes (Optum #FBF9F4, Dark #121212, UHC, Rally, etc.).
  const themePageBg: string = theme?.colors?.quaternary ?? '#ffffff';

  const { pageBackground } = currentPage ?? {};
  const cardBackground: string = pageBackground?.color || themePageBg;

  // Always compute text color from background luminance so dark-on-dark never
  // happens even when theme.colors.text.primary is incorrect or absent.
  const textColor: string = getContrastColor(
    cardBackground,
    '#FFFFFF',
    '#1A1A1A'
  );
  const isDarkBg = textColor === '#FFFFFF';

  // Pull link/accent from the theme's custom tokens when available (dark theme
  // stores it as { id: 'link', value: '#8AB4F8' }); fall back to theme.colors.primary.
  const customLink: string | undefined = (theme?.colors?.custom ?? []).find(
    (c: any) => c.id === 'link'
  )?.value;
  const accentColor: string =
    customLink ?? (isDarkBg ? '#8AB4F8' : theme?.colors?.primary ?? '#0C55B8');

  // Fetch results whenever the query or fetch function reference changes.
  useEffect(() => {
    setLoading(true);
    fetchSearchResults(searchQuery)
      .then(data => setResults(data || []))
      .catch(err => {
        setError(err);
        setResults([]);
      })
      .finally(() => setLoading(false));
  }, [searchQuery, fetchSearchResults]);

  // Fire the page-load analytics event separately so the data-fetch effect
  // does not need to include the tracker in its dependency array.
  useEffect(() => {
    trackSearchResults('search_widget_results_page_load', {
      query: searchQuery,
    });
    // trackSearchResults reference is stable for the lifetime of the page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  const sortedResults = useMemo(() => {
    return [...results].sort((a, b) => {
      switch (sortBy) {
        case 'date_updated':
          return (
            new Date(b.dateUpdated).getTime() -
            new Date(a.dateUpdated).getTime()
          );
        case 'title_asc':
          return a.title.localeCompare(b.title);
        case 'title_desc':
          return b.title.localeCompare(a.title);
        default:
          return 0;
      }
    });
  }, [results, sortBy]);

  const paginationConfig = { totalItemsCount: sortedResults.length };
  const entriesPerPageConfig = {
    pageSizeOptions: [
      { label: '3', value: 3 },
      { label: '6', value: 6 },
      { label: '9', value: 9 },
    ],
    initialValue: 3,
  };

  return (
    <ResultsContainer pageBackground={cardBackground} textColor={textColor}>
      <Title>
        Search results for:{' '}
        <span style={{ color: accentColor }}>{searchQuery}</span>
      </Title>
      <ControlsContainer>
        <FormControl id={'sortBy-select'}>
          <SortDropdown
            data-test-id={'sort-dropdown-search'}
            value={sortBy}
            onChange={e => setSortBy(e.target.value)}
            pageBackground={cardBackground}
            textColor={textColor}
          >
            <option value="relevance">Sort: Relevance</option>
            <option value="date_updated">Date Updated</option>
            <option value="title_asc">Title A-Z</option>
            <option value="title_desc">Title Z-A</option>
          </SortDropdown>
        </FormControl>

        <SearchBarWidget
          fetchSearchResults={fetchSearchResults}
          site={site}
          widgetState={searchWidgetState}
          currentPage={currentPage}
        />
      </ControlsContainer>

      {loading && <div>Loading...</div>}
      {error && <div>Error fetching results.</div>}
      {sortedResults.length === 0 && !loading && !error && (
        <div>No results found.</div>
      )}
      <Pagination
        id="pagination-entries"
        entriesPerPageConfig={entriesPerPageConfig}
        config={paginationConfig}
      >
        {({ page, pageSize }: IPaginationState) => (
          <ResultsList>
            {sortedResults
              .slice((page - 1) * pageSize, page * pageSize)
              .map(result => (
                <SearchResultCard
                  key={result.link || result.title}
                  result={result}
                  searchQuery={searchQuery}
                  cardBackground={cardBackground}
                  accentColor={accentColor}
                  textColor={textColor}
                  onResultClick={() =>
                    trackSearchResults('search_widget_result_click', {
                      query: searchQuery,
                      resultTitle: result.title,
                      resultCategory: result.category,
                      page,
                    })
                  }
                />
              ))}
          </ResultsList>
        )}
      </Pagination>
    </ResultsContainer>
  );
};

export default SearchResults;
